// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Plus, X, Save, Trash2 } from 'lucide-react';

const packageSchema = z.object({
  name: z.string().min(1, 'Package name is required'),
  package_type: z.string().min(1, 'Package type is required'),
  price: z.number().min(0.01, 'Price must be greater than 0'),
  description: z.string().optional(),
  is_active: z.boolean().default(true),
});

type PackageFormData = z.infer<typeof packageSchema>;

interface GraphicsPackage {
  id: string;
  vendor_id: string;
  name: string;
  package_type: string;
  price: number;
  description?: string;
  features?: string[];
  is_active: boolean;
  created_at: string;
}

interface GraphicsPackageFormProps {
  vendorId: string;
  package?: GraphicsPackage | null;
  onPackageUpdate: () => void;
  onCancel?: () => void;
}

const GraphicsPackageForm: React.FC<GraphicsPackageFormProps> = ({
  vendorId,
  package: existingPackage,
  onPackageUpdate,
  onCancel,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [features, setFeatures] = useState<string[]>([]);
  const [newFeature, setNewFeature] = useState('');
  const isEditing = !!existingPackage;

  const form = useForm<PackageFormData>({
    resolver: zodResolver(packageSchema),
    defaultValues: {
      name: '',
      package_type: '',
      price: 0,
      description: '',
      is_active: true,
    },
  });

  useEffect(() => {
    if (existingPackage) {
      form.reset({
        name: existingPackage.name || '',
        package_type: existingPackage.package_type || '',
        price: existingPackage.price || 0,
        description: existingPackage.description || '',
        is_active: existingPackage.is_active,
      });
      setFeatures(existingPackage.features || []);
    } else {
      form.reset({
        name: '',
        package_type: '',
        price: 0,
        description: '',
        is_active: true,
      });
      setFeatures([]);
    }
  }, [existingPackage, form]);

  const addFeature = () => {
    if (newFeature.trim()) {
      setFeatures([...features, newFeature.trim()]);
      setNewFeature('');
    }
  };

  const removeFeature = (index: number) => {
    setFeatures(features.filter((_, i) => i !== index));
  };

  const onSubmit = async (data: PackageFormData) => {
    try {
      setIsSaving(true);

      // Ensure name is not empty
      if (!data.name || data.name.trim() === '') {
        toast.error('Package name is required');
        return;
      }

      const packageData = {
        ...data,
        name: data.name.trim(),
        vendor_id: vendorId,
        features: features.length > 0 ? features : [],
      };

      if (isEditing && existingPackage) {
        const { error } = await supabase
          .from('graphics_packages')
          .update(packageData)
          .eq('id', existingPackage.id);

        if (error) throw error;
        toast.success('Package updated successfully');
      } else {
        const { error } = await supabase
          .from('graphics_packages')
          .insert(packageData);

        if (error) throw error;
        toast.success('Package created successfully');
      }

      form.reset();
      setFeatures([]);
      onPackageUpdate();
      onCancel?.();
    } catch (error: any) {
      console.error('Error saving package:', error);
      toast.error(error.message || 'Failed to save package');
    } finally {
      setIsSaving(false);
    }
  };

  const deletePackage = async () => {
    if (!existingPackage) return;

    try {
      setIsSaving(true);
      
      const { error } = await supabase
        .from('graphics_packages')
        .delete()
        .eq('id', existingPackage.id);

      if (error) throw error;

      toast.success('Package deleted successfully');
      onPackageUpdate();
      onCancel?.();
    } catch (error: any) {
      console.error('Error deleting package:', error);
      toast.error(error.message || 'Failed to delete package');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {isEditing ? 'Edit Graphics Package' : 'Create Graphics Package'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Package Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Package Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Premium Logo Package, Basic Business Cards" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Package Type and Price - Inline Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="package_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Package Type *</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Logo Design, Business Card, Banner" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price ($) *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="text"
                          placeholder="0.00"
                          className="pl-6 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^0-9.]/g, '');
                            
                            // Prevent multiple decimal points
                            const decimalCount = (value.match(/\./g) || []).length;
                            if (decimalCount > 1) {
                              value = value.substring(0, value.lastIndexOf('.'));
                            }
                            
                            // Limit to 2 decimal places
                            const parts = value.split('.');
                            if (parts[1] && parts[1].length > 2) {
                              value = `${parts[0]}.${parts[1].substring(0, 2)}`;
                            }
                            
                            if (value === '' || value === '.') {
                              field.onChange(0);
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue)) {
                                field.onChange(numValue);
                              }
                            }
                          }}
                          value={field.value > 0 ? field.value.toString() : ''}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe what's included in this package..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Features Section */}
            <div className="space-y-2">
              <Label>Features</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="Add a feature..."
                  value={newFeature}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                />
                <Button type="button" onClick={addFeature} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {features.length > 0 && (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                      <span className="text-sm">{feature}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active Package</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Make this package available for customers to order
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-between space-x-2 pt-4">
              {isEditing && (
                <Button
                  type="button"
                  variant="destructive"
                  onClick={deletePackage}
                  disabled={isSaving}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Package
                </Button>
              )}
              <div className="flex space-x-2 ml-auto">
                {onCancel && (
                  <Button type="button" variant="outline" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
                <Button type="submit" disabled={isSaving}>
                  <Save className="h-4 w-4 mr-2" />
                  {isSaving ? 'Saving...' : isEditing ? 'Update Package' : 'Create Package'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default GraphicsPackageForm;